import {
  htmlToProsemirrorNode,
  prosemirrorNodeToHtml,
  EditorState,
} from 'remirror';
import {
  EditorComponent,
  Remirror,
  useKeymap,
  useRemirror,
} from '@remirror/react';
import {
  HistoryExtension,
  BoldExtension,
  ItalicExtension,
  CalloutExtension,
  UnderlineExtension,
  StrikeExtension,
  HeadingExtension,
  ImageExtension,
  DropCursorExtension,
  HorizontalRuleExtension,
  OrderedListExtension,
  BulletListExtension,
  ListItemExtension,
  HardBreakExtension,
  NodeFormattingExtension,
  LinkExtension,
  CodeBlockExtension,
  TrailingNodeExtension,
  BlockquoteExtension,
} from 'remirror/extensions';
import { Tooltip } from '@legacy/components/mui-components/Tooltip';
import { useTranslation } from 'react-i18next';
import { HTMLAttributes, ReactElement } from 'react';
import { Button, Stack } from '@legacy/components/mui-components';
import { Menu } from './components';

import sharedRichTextStyles from '../RichText.module.scss';
import styles from './RichTextEditor.module.scss';
import { useBuildShortcutString } from './components/Menu/helpers';

interface SharedProps {
  saveFunction: (html: string) => void;
  exitFunction: () => void;
}

interface HooksProps extends SharedProps {
  state: Readonly<EditorState>;
}

const hooks = ({ state, saveFunction, exitFunction }: HooksProps) => [
  () => {
    const handleSaveShortcut = () => {
      saveFunction(prosemirrorNodeToHtml(state.doc));
      exitFunction();
      return true; // Prevents any further key handlers from being run.
    };

    const handleEscapeShortcut = () => {
      exitFunction();
      return true;
    };

    // "Mod" means platform agnostic modifier key - i.e. Ctrl on Windows, or Cmd on MacOS
    useKeymap('Mod-s', handleSaveShortcut);
    useKeymap('Escape', handleEscapeShortcut);
  },
];

const extensions = () => [
  new HistoryExtension({}),
  new LinkExtension({ autoLink: true }),
  new HardBreakExtension(),
  new HeadingExtension({}),
  new BoldExtension({}),
  new ItalicExtension(),
  new UnderlineExtension(),
  new StrikeExtension(),
  new HorizontalRuleExtension({}),
  new OrderedListExtension(),
  new BulletListExtension({}),
  new ListItemExtension({}),
  new ImageExtension({
    enableResizing: true,
    extraAttributes: { loading: 'lazy' },
  }),
  new CalloutExtension({ defaultType: 'warn' }),
  new CodeBlockExtension({}),
  new TrailingNodeExtension({}),
  new NodeFormattingExtension({}),
  new DropCursorExtension({}),
  new BlockquoteExtension(),
];

interface RichTextEditorProps extends SharedProps, HTMLAttributes<HTMLElement> {
  value: string;
}

export const RichTextEditor = ({
  value,
  saveFunction,
  exitFunction,
  ...props
}: RichTextEditorProps): ReactElement => {
  const { manager, state, onChange } = useRemirror({
    selection: 'start',
    extensions,
    content: value,
    stringHandler: htmlToProsemirrorNode,
  });

  const { t } = useTranslation('richTextEditor');

  const shortcutString = useBuildShortcutString();

  const EditorComponentAsReactComponent =
    EditorComponent as unknown as React.ComponentType;

  return (
    <Stack {...props}>
      <div className={styles.Wrapper}>
        <Remirror
          manager={manager}
          autoFocus
          onChange={onChange}
          initialContent={state}
          hooks={hooks({ state, saveFunction, exitFunction })}
        >
          <>
            <Menu className={styles.Menu} />
            <div className={sharedRichTextStyles.RichText}>
              <EditorComponentAsReactComponent data-automation-id="RichTextEditorComponent" />
            </div>
          </>
        </Remirror>
      </div>
      <Stack direction="row" justifyContent="right">
        <Tooltip title={`${t('CancelEditorChangesLabel')} (Esc)`}>
          <Button data-automation-id="btngExitEditor" onClick={exitFunction}>
            {t('CancelEditorChangesLabel')}
          </Button>
        </Tooltip>
        <Tooltip title={shortcutString(t('SaveEditorChangesLabel'), 'S')}>
          <Button
            variant="contained"
            data-automation-id="btnpSaveAndExitEditor"
            onClick={() => {
              saveFunction(prosemirrorNodeToHtml(state.doc));
              exitFunction();
            }}
          >
            {t('SaveEditorChangesLabel')}
          </Button>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
